import React from 'react';
import { API_BASE, toUrl } from '../lib/config';


type Partner = {
  id: string;
  name: string;
  logoUrl: string | null;
  category: string;
  description?: string | null;
  href?: string | null;
  orderIndex: number;
  isActive: boolean;
  updatedAt: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const AnlasmaliPartnersYonetim: React.FC = () => {

  const [items, setItems] = React.useState<Partner[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Media options for logo selection
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  // Create form
  const [newName, setNewName] = React.useState('');
  const [newCategory, setNewCategory] = React.useState('');
  const [newOrder, setNewOrder] = React.useState<number>(0);
  const [newActive, setNewActive] = React.useState(true);
  const [newHref, setNewHref] = React.useState('');
  const [newDesc, setNewDesc] = React.useState('');
  const [newLogoUrl, setNewLogoUrl] = React.useState<string>('');
  const [creating, setCreating] = React.useState(false);

  // Edit form state
  const [editingId, setEditingId] = React.useState<string>('');
  const [editName, setEditName] = React.useState('');
  const [editCategory, setEditCategory] = React.useState('');
  const [editOrder, setEditOrder] = React.useState<number>(0);
  const [editActive, setEditActive] = React.useState<boolean>(true);
  const [editHref, setEditHref] = React.useState<string>('');
  const [editDesc, setEditDesc] = React.useState<string>('');
  const [editLogoUrl, setEditLogoUrl] = React.useState<string>('');
  const [saving, setSaving] = React.useState(false);

  // Delete confirm
  const [confirmId, setConfirmId] = React.useState<string>('');
  const [confirmLoading, setConfirmLoading] = React.useState<boolean>(false);
  const [deletingId, setDeletingId] = React.useState<string>('');
  const [q, setQ] = React.useState<string>('');

  const fetchList = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(toUrl('/api/anlasmali-partners/admin'));
      if (!res.ok) throw new Error('Liste alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMediaOpts = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      const opts: MediaOpt[] = (data || []).map((m: any) => ({ id: m.id, name: m.name || m.filename || m.url, url: m.url }));
      setMediaOpts(opts);
    } catch {
      // ignore
    }
  }, []);

  React.useEffect(() => { fetchList(); fetchMediaOpts(); }, [fetchList, fetchMediaOpts]);

  const filteredItems = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) => {
      const name = (p.name || '').toLowerCase();
      const cat = (p.category || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      const href = (p.href || '').toLowerCase();
      return name.includes(term) || cat.includes(term) || desc.includes(term) || href.includes(term);
    });
  }, [items, q]);

  const resetCreate = () => {
    setNewName('');
    setNewCategory('');
    setNewOrder(0);
    setNewActive(true);
    setNewHref('');
    setNewDesc('');
    setNewLogoUrl('');
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newCategory.trim()) { setError('Lütfen Ad ve Kategori girin'); return; }
    try {
      setCreating(true);
      setError('');
      const body: any = {
        name: newName.trim(),
        category: newCategory.trim(),
        orderIndex: Number(newOrder) || 0,
        isActive: !!newActive,
      };
      if (newHref.trim()) body.href = newHref.trim();
      if (newDesc.trim()) body.description = newDesc.trim();
      if (newLogoUrl) body.logoUrl = newLogoUrl;
      const res = await fetch(toUrl('/api/anlasmali-partners/admin'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreate();
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (p: Partner) => {
    setEditingId(p.id);
    setEditName(p.name);
    setEditCategory(p.category);
    setEditOrder(p.orderIndex);
    setEditActive(p.isActive);
    setEditHref(p.href || '');
    setEditDesc(p.description || '');
    setEditLogoUrl(p.logoUrl || '');
  };
  const cancelEdit = () => {
    setEditingId('');
    setEditName('');
    setEditCategory('');
    setEditOrder(0);
    setEditActive(true);
    setEditHref('');
    setEditDesc('');
    setEditLogoUrl('');
  };

  const onSave = async (id: string) => {
    if (!editName.trim() || !editCategory.trim()) { setError('Lütfen Ad ve Kategori girin'); return; }
    try {
      setSaving(true);
      setError('');
      const body: any = {
        name: editName.trim(),
        category: editCategory.trim(),
        orderIndex: Number(editOrder) || 0,
        isActive: !!editActive,
        href: editHref.trim() || null,
        description: editDesc.trim() || null,
        logoUrl: editLogoUrl || null,
      };
      const res = await fetch(toUrl(`/api/anlasmali-partners/admin/${id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit();
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const onDelete = (id: string) => setConfirmId(id);
  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      setConfirmLoading(true);
      setDeletingId(confirmId);
      const res = await fetch(toUrl(`/api/anlasmali-partners/admin/${confirmId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setConfirmId('');
      await fetchList();
    } catch (e: any) {
      setError(e.message || 'Beklenmeyen bir hata oluştu');
    } finally {
      setConfirmLoading(false);
      setDeletingId('');
    }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Anlaşmalı Partnerler</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      {/* Create form */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>Yeni Partner Ekle</h3>
        <form onSubmit={onCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Row 1 */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Ad</label>
            <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} className="input" required />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Kategori</label>
            <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} className="input" placeholder="örn. indirim/sigorta" required />
          </div>
          {/* Row 2 */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Sıra</label>
            <input type="number" value={newOrder} onChange={(e) => setNewOrder(Number(e.target.value))} className="input" style={{ maxWidth: 140 }} min={0} />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Aktif</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id="newActive" type="checkbox" checked={newActive} onChange={(e) => setNewActive(e.target.checked)} />
              <label htmlFor="newActive" style={{ userSelect: 'none' }}>Aktif</label>
            </div>
          </div>
          {/* Row 3: Link + Logo yan yana */}
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Link (opsiyonel)</label>
            <input type="url" value={newHref} onChange={(e) => setNewHref(e.target.value)} className="input" placeholder="https://..." />
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label className="form-label">Logo (Medya)</label>
            <select value={newLogoUrl} onChange={(e) => setNewLogoUrl(e.target.value)} className="input" style={{ width: '100%' }}>
              <option value="">Seçiniz</option>
              {mediaOpts.map((m) => (
                <option key={m.url} value={m.url}>{m.name}</option>
              ))}
            </select>
          </div>
          {/* Row 5 (span 2): Description at bottom */}
          <div style={{ display: 'grid', gap: 6, gridColumn: '1 / -1' }}>
            <label className="form-label">Açıklama (opsiyonel)</label>
            <textarea value={newDesc} onChange={(e) => setNewDesc(e.target.value)} className="input" rows={3} />
          </div>
          {/* Submit Row (full width, right aligned) */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
            <button type="submit" disabled={creating} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creating ? 0.8 : 1, minWidth: 120 }}>
              {creating ? 'Ekleniyor...' : 'Ekle'}
            </button>
          </div>
        </form>
        {newLogoUrl && (
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>Önizleme:</div>
            <img
              src={toUrl(newLogoUrl)}
              alt="logo"
              style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}
              crossOrigin="anonymous"
              onError={(e) => {
                const el = e.currentTarget as HTMLImageElement;
                const current = el.getAttribute('src') || '';
                const raw = newLogoUrl;
                const pref = toUrl(newLogoUrl);
                const next = current === pref ? raw : pref;
                if (current !== next) el.src = next;
              }}
            />
          </div>
        )}
        {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
      </div>

      {/* List table */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ara: ad, kategori, açıklama, link"
              className="input"
              style={{ width: 280 }}
            />
            {q && (
              <button onClick={() => setQ('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>
            )}
          </div>
          <button onClick={fetchList} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Logo</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Ad</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Kategori</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Açıklama</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Link</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>
              )}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>
              )}
              {filteredItems.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px' }}>
                    {p.logoUrl ? (
                      <img
                        src={toUrl(p.logoUrl)}
                        alt={p.name}
                        style={{ width: 120, height: 60, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}
                        crossOrigin="anonymous"
                        onError={(e) => {
                          const el = e.currentTarget as HTMLImageElement;
                          const current = el.getAttribute('src') || '';
                          const raw = p.logoUrl as string;
                          const pref = toUrl(p.logoUrl as string);
                          const next = current === pref ? raw : pref;
                          if (current !== next) el.src = next;
                        }}
                      />
                    ) : <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === p.id ? (
                      <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="input" />
                    ) : (
                      p.name
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === p.id ? (
                      <input type="text" value={editCategory} onChange={(e) => setEditCategory(e.target.value)} className="input" />
                    ) : (
                      p.category
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === p.id ? (
                      <input type="number" value={editOrder} onChange={(e) => setEditOrder(Number(e.target.value))} className="input" style={{ width: 100 }} />
                    ) : (
                      p.orderIndex
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    {editingId === p.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input id={`editActive-${p.id}`} type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} />
                        <label htmlFor={`editActive-${p.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 999,
                          fontSize: 12,
                          background: p.isActive ? '#ecfdf5' : '#fef2f2',
                          color: p.isActive ? '#065f46' : '#991b1b',
                          border: `1px solid ${p.isActive ? '#a7f3d0' : '#fecaca'}`,
                        }}
                      >
                        {p.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', maxWidth: 300 }}>
                    {editingId === p.id ? (
                      <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} className="input" rows={3} />
                    ) : (
                      <div
                        title={p.description || ''}
                        style={{
                          color: '#374151',
                          fontSize: 13,
                          lineHeight: 1.4,
                          maxHeight: 100,
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          border: '1px solid #e5e7eb',
                          borderRadius: 6,
                          padding: 8,
                          background: '#fff',
                        }}
                      >
                        {(p.description || '-') && (p.description ? (p.description.length > 250 ? (p.description.slice(0, 250) + '…') : p.description) : '-')}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', maxWidth: 240 }}>
                    {editingId === p.id ? (
                      <input type="url" value={editHref} onChange={(e) => setEditHref(e.target.value)} className="input" placeholder="https://..." />
                    ) : (
                      p.href ? <a href={p.href} target="_blank" rel="noreferrer" style={{ color: '#2563eb' }}>{p.href}</a> : <span style={{ color: '#9ca3af' }}>-</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === p.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select value={editLogoUrl} onChange={(e) => setEditLogoUrl(e.target.value)} className="input">
                            <option value="">Logo: Seçiniz</option>
                            {mediaOpts.map((m) => (
                              <option key={m.url} value={m.url}>{m.name}</option>
                            ))}
                          </select>
                          {editLogoUrl && (
                            <img
                              src={toUrl(editLogoUrl)}
                              alt="logo"
                              style={{ width: 96, height: 48, objectFit: 'contain', border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff' }}
                              crossOrigin="anonymous"
                              onError={(e) => {
                                const el = e.currentTarget as HTMLImageElement;
                                const current = el.getAttribute('src') || '';
                                const raw = editLogoUrl;
                                const pref = toUrl(editLogoUrl);
                                const next = current === pref ? raw : pref;
                                if (current !== next) el.src = next;
                              }}
                            />
                          )}
                        </div>
                        <button onClick={cancelEdit} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>İptal</button>
                        <button onClick={() => onSave(p.id)} disabled={saving} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: saving ? 0.8 : 1 }}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(p)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                        <button onClick={() => onDelete(p.id)} disabled={deletingId === p.id} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: deletingId === p.id ? 0.8 : 1 }}>Sil</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirm Modal (custom, no Bootstrap) */}
      {confirmId && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="partnerDeleteModalTitle"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmId(''); }}
          onKeyDown={(e) => { if (e.key === 'Escape') setConfirmId(''); }}
          tabIndex={-1}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(420px, 92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="partnerDeleteModalTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setConfirmId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu partneri silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmId('')} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer', opacity: confirmLoading ? 0.7 : 1 }}>İptal</button>
              <button type="button" onClick={confirmDelete} disabled={confirmLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: confirmLoading ? 0.8 : 1 }}>{confirmLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnlasmaliPartnersYonetim;
